import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow } from 'date-fns';
import { de, enGB } from 'date-fns/locale';
import type { Locale } from 'date-fns';

/**
 * Date formatting that follows the selected language.
 *
 * Without this the app was inconsistent in both directions: most pages used
 * date-fns' English default regardless of language, while ImagePicker had the
 * German locale hard-coded. Switching to German left "3 hours ago" in place;
 * switching to English still showed "vor 3 Stunden" in the release form.
 *
 * enGB rather than enUS: the rest of the app writes day-first dates, and mixing
 * 04.08.2026 with 8/4/2026 in one table is worse than either alone.
 */
const LOCALES: Record<string, Locale> = { de, en: enGB };

export function useDateFormat() {
  const { i18n } = useTranslation();
  const locale = LOCALES[i18n.resolvedLanguage ?? 'en'] ?? enGB;

  return {
    locale,
    /** "04.08.2026 00:25" / "04/08/2026 00:25" */
    dateTime: (value: string | Date | null | undefined): string =>
      value ? format(new Date(value), 'P p', { locale }) : '—',
    /** "04.08.2026" */
    date: (value: string | Date | null | undefined): string =>
      value ? format(new Date(value), 'P', { locale }) : '—',
    /** "vor 3 Stunden" / "3 hours ago" */
    relative: (value: string | Date | null | undefined): string =>
      value ? formatDistanceToNow(new Date(value), { addSuffix: true, locale }) : '—',
  };
}
